import dynamic from 'next/dynamic';
import { signOut, useSession } from 'next-auth/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import useIdentifyMetrics from '../hooks/useIdentifyMetrics';
import { useMetrics } from '../hooks/useMetrics';
import useSignOut from '../hooks/useSignOut';
import BackendService from '../services/backendService';
import { AUTH_EVENTS, clearAuthPending, readAuthPending } from '../utils/authFunnel';
import { isProduction } from '../utils/client';
import { getEndImpersonationCallbackUrl } from '../utils/endImpersonationRedirect';
import { configureStore } from '../utils/store';

const ImpersonationBanner = dynamic(
  () => import('../components/_organisms/ImpersonationBanner/ImpersonationBanner'),
  { ssr: false },
);

const MetricsIdentifier = ({ uid }: { uid: string }) => {
  useIdentifyMetrics(uid);
  return null;
};

const BackendServiceProvider = ({ children }: { children: React.ReactNode }) => {
  const session = useSession();
  const { signOutAuto } = useSignOut();
  const metrics = useMetrics(true);
  const { config } = useMemo(() => configureStore(), []);

  useState(() => {
    new BackendService().init(config.backend.externalApiUrl, config.cdn.url);
    if (session && session.data?.idToken) {
      BackendService.instance.setToken(session.data.idToken as string);
      BackendService.instance.setProvider(session.data.provider as string | undefined);
    }
  });

  useEffect(() => {
    if (session && session.data?.idToken) {
      new BackendService();
      BackendService.instance.setToken(session.data.idToken as string);
      // Keep the provider in sync so a 401-triggered refresh picks the right path
      // (NextAuth rotation for new-auth, legacy endpoint for B2C).
      BackendService.instance.setProvider(session.data.provider as string | undefined);
    }
  }, [config.backend.externalApiUrl, config.cdn.url, session]);

  // One-shot guard so a session that keeps re-resolving with the same error
  // doesn't fire competing sign-outs.
  const recoveryStarted = useRef(false);

  useEffect(() => {
    const error = session?.data?.error;
    if (!error || recoveryStarted.current) return;

    // The auth pages own errored-session handling: their GSSP clears the cookie and
    // they render the form / error UI. Skip the global recovery there — otherwise a
    // briefly-cached errored client session would yank the user off /login (or
    // /signup, /auth/error) back to /auth/error and steal their retry.
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (path === '/login' || path === '/signup' || path === '/auth/error') return;

    // New-auth provisioning failed (exchange/self-join returned no session). Sign
    // out and land on the styled /auth/error page with the specific reason, rather
    // than a silent sign-out that leaves the user with no explanation.
    if (error === 'ProvisioningError') {
      recoveryStarted.current = true;
      const type = session?.data?.authErrorType ?? 'oauthCallbackError';
      const correlationId = session?.data?.correlationId;
      const url = new URL(`${window.location.origin}/auth/error`);
      url.searchParams.set('error_type', type);
      if (correlationId) url.searchParams.set('correlation_id', correlationId);
      // Preserve the non-prod QA override so staging testers who enabled the new
      // experience via ?newauth=1 still get AuthErrorView, not the legacy modal.
      if (!isProduction()) url.searchParams.set('newauth', '1');
      signOutAuto(url.toString());
      return;
    }

    if (error === 'RefreshAccessTokenError') {
      recoveryStarted.current = true;
      if (session?.data?.isImpersonation) {
        signOut({ callbackUrl: getEndImpersonationCallbackUrl() });
      } else {
        // Provider-aware sign-out: clears the NextAuth session (and the B2C
        // session cookie for legacy B2C users), then lands on /login so the user
        // can pick how to sign in. A plain signIn() here would silently re-auth
        // via SSO and leave them in the same broken state.
        signOutAuto(`${window.location.origin}/login`);
      }
    }
  }, [session, signOutAuto]);

  // Signup funnel: close the loop once auth actually succeeds. Pairs the pending
  // marker (set at initiation, surviving the OAuth redirect) with isNewUser from
  // the resolved session, so Auth.Succeeded fires once per attempt — across every
  // flow — and distinguishes signups (incl. a login click that created an account).
  useEffect(() => {
    if (session.status !== 'authenticated') return;
    const data = session.data;
    if (!data?.idToken || data.error) return; // only a genuine, usable login
    const pending = readAuthPending();
    if (!pending) return;
    metrics.logEvent(AUTH_EVENTS.succeeded, {
      method: pending.method,
      provider: pending.provider,
      intendedMode: pending.mode,
      isNewUser: !!data.isNewUser,
    });
    clearAuthPending();
  }, [session, metrics]);

  return (
    <>
      {session?.data?.isImpersonation && <ImpersonationBanner />}
      {session?.data?.user?.uid && <MetricsIdentifier uid={session.data.user.uid} />}
      {children}
    </>
  );
};

export default BackendServiceProvider;
